import { faCalendarDays, faClock } from '@fortawesome/free-regular-svg-icons'
import { parseISO } from 'date-fns'
import { useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'

import RefereeActionButtons from '@/components/ActionButtons/RefereeActionButtons'
import DetailPill from '@/components/DetailPill'
import Details from '@/components/Details'
import DetailsCard from '@/components/DetailsCard'
import LandlordReferencePanel from '@/components/EntityPanels/LandlordCard/LandlordReferencePanel'
import ExpectedError from '@/components/ExpectedError'
import { Header } from '@/components/Header'
import PageContainer from '@/components/PageContainer'
import useLandlordReferee from '@/hooks/useLandlordReferee'
import useUser from '@/hooks/useUser'
import { useSignalREventHandler } from '@/lib/signalR'
import { EVENT_TYPES } from '@/lib/signalR/hooks/useSignalREventHandler'
import type {
  EventHandler,
  ShouldHandleEvent,
} from '@/lib/signalR/hooks/useSignalREventHandler'
import {
  getQueueDayZeroBeginning,
  hasAgeOfReferenceWarning,
  toAgeOfReferenceLabel,
} from '@/utils/ageOfReference'
import { formatDate } from '@/utils/date'
import { isLandlord } from '@/utils/subjectHelpers'
import { Layout, Container, ColumnContainer, PanelContainer } from '@/views/Applicant'

interface Params {
  subjectId: string
}

const DetailPillsContainer = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 170px 170px;
`

const LandlordRefereePage = () => {
  const history = useHistory()

  const { subjectId } = useParams<Params>()

  const { customerId } = useUser()

  const { loading, error, application, fetchSingleApplication, landlordReferee } =
    useLandlordReferee()

  const onClose = () => history.goBack()

  const shouldHandleSignalREvent = useCallback<ShouldHandleEvent>(
    event => application?.subjectId === event.SubjectId,
    [application],
  )

  const handleSignalREvent = useCallback<EventHandler>(
    event => {
      switch (event.EventType) {
        case EVENT_TYPES.ReportGenerated:
        case EVENT_TYPES.SubjectOutcomeOverridden:
          fetchSingleApplication(event.CustomerId, event.SubjectId)
          break
      }
    },
    [fetchSingleApplication],
  )

  useSignalREventHandler({
    shouldHandleEvent: shouldHandleSignalREvent,
    eventHandler: handleSignalREvent,
  })

  useEffect(() => {
    if (customerId) {
      fetchSingleApplication(customerId, subjectId)
    }
  }, [fetchSingleApplication, subjectId, customerId])

  const breadcrumbs = [
    {
      label: 'Referees',
    },
    {
      label: 'Landlord',
    },
  ]

  const queueDayZeroBeginning = getQueueDayZeroBeginning(application?.milestones)

  const tenancyStartDate =
    application?.assets && application.assets[0].attributes.startDate

  if (error !== undefined) {
    return (
      <ExpectedError
        text={error}
        imagePath="/assets/svg/failed-to-retrieve-applicant.svg"
      />
    )
  }

  return (
    <>
      <Header
        isLoading={loading}
        hasError={error !== undefined}
        name={application?.name?.toLowerCase() ?? ''}
        subjectId={application?.subjectId}
        breadcrumbs={breadcrumbs}
      />
      <PageContainer>
        {loading
          ? 'Loading...'
          : application &&
            (!isLandlord(application.type) ? (
              <>The subject is not a landlord.</>
            ) : (
              <Layout>
                <Details
                  application={application}
                  actionButtons={
                    landlordReferee !== undefined && (
                      <RefereeActionButtons
                        refereeType="landlord"
                        referee={landlordReferee}
                      />
                    )
                  }
                  detailPills={
                    <DetailPillsContainer>
                      {tenancyStartDate && (
                        <DetailPill
                          icon={faCalendarDays}
                          title="Tenancy start date:"
                          detail={formatDate(parseISO(tenancyStartDate))}
                        />
                      )}
                      <DetailPill
                        icon={faClock}
                        title="Age of reference:"
                        detail={toAgeOfReferenceLabel(queueDayZeroBeginning)}
                        warning={hasAgeOfReferenceWarning(queueDayZeroBeginning)}
                      />
                    </DetailPillsContainer>
                  }
                />
                <Container>
                  <ColumnContainer>
                    <PanelContainer>
                      <LandlordReferencePanel application={application} />
                    </PanelContainer>
                  </ColumnContainer>
                  <ColumnContainer>
                    <PanelContainer>
                      <DetailsCard
                        type="Landlord"
                        applicationId={application.applicationId}
                        subjectId={application.subjectId}
                        agencyName={application.customerClientName}
                        onClose={onClose}
                        application={application}
                        fetchSingleApplication={fetchSingleApplication}
                        alwaysExpand
                      />
                    </PanelContainer>
                  </ColumnContainer>
                </Container>
              </Layout>
            ))}
      </PageContainer>
    </>
  )
}

export default LandlordRefereePage
